import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { isEqual } from "lodash";

import TagInput from "components/ui/tag_input/TagInput";

type JobTitleFilterValue = { jobTitles: string[] };

export type JobTitleFilterProps = {
  value: string[];
  onChange: (value: string[]) => void;
};

/**
 * Free-text job-title keyword filter for the contacts drawer. Entered as chips
 * (Enter/comma/semicolon, paste-split, dedupe) and matched server-side by the shared
 * keyword matcher — quotes, AND/OR/(), acronyms — identical to signals/notices keywords.
 * `TagInput` is a react-hook-form field, so we host a local form and sync its value out
 * to the drawer's plain value/onChange state (mirrors CognismContactsBuyersFilter).
 */
function JobTitleFilter({ value, onChange }: JobTitleFilterProps) {
  const { control, watch, reset, getValues } = useForm<JobTitleFilterValue>({
    defaultValues: { jobTitles: value },
  });
  // The last value reconciled in either direction — guards both effects from looping.
  const lastSynced = useRef<string[]>(value);
  // Latest onChange in a ref so the watch subscription isn't torn down on every parent render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Form edits → parent.
  useEffect(() => {
    const subscription = watch(() => {
      const next = getValues().jobTitles;
      if (!isEqual(next, lastSynced.current)) {
        lastSynced.current = next;
        onChangeRef.current(next);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues]);

  // External replace (e.g. the drawer's "Clear all") → form; skipped for our own edits.
  useEffect(() => {
    if (!isEqual(value, lastSynced.current)) {
      lastSynced.current = value;
      reset({ jobTitles: value });
    }
  }, [value, reset]);

  return <TagInput name="jobTitles" control={control} placeholder="Search job titles" />;
}

export default JobTitleFilter;
